import axios from 'axios';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

export type ExpenseCategory = 'MEAL' | 'TRANSPORT' | 'ACCOMMODATION' | 'OTHER';
export type ExpenseStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';

export interface ExpenseAttachment {
  id?: number;
  expenseClaimId?: number;
  fileUrl: string;
  mimeType: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExpenseClaim {
  id: number;
  userId: number;
  tenantId: number;
  date: string;
  category: ExpenseCategory;
  amount: number;
  hasReceipt: boolean;
  declaredWithoutReceipt: boolean;
  status: ExpenseStatus;
  notes?: string | null;
  reviewedById?: number | null;
  reviewedAt?: string | null;
  reviewNote?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  attachments: ExpenseAttachment[];
  user?: { id: number; name: string; email?: string };
}

export interface CreateExpenseClaimDto {
  date: string;
  category: ExpenseCategory;
  amount: number;
  hasReceipt: boolean;
  declaredWithoutReceipt: boolean;
  notes?: string;
  attachments?: Array<{ fileUrl: string; mimeType: string }>;
}

export interface UpdateExpenseClaimDto extends Partial<CreateExpenseClaimDto> {}

export interface UploadedExpenseAttachment {
  fileUrl: string;
  mimeType: string;
}

export const expenseClaimsService = {
  getMine: () => axios.get<ExpenseClaim[]>(`${API_URL}/expense-claims/mine`).then((r) => r.data),

  /** `onlyMine` narrows to claims from people whose direct approver is the caller. */
  getPending: (onlyMine = false) =>
    axios
      .get<ExpenseClaim[]>(`${API_URL}/expense-claims/pending`, { params: onlyMine ? { onlyMine: 1 } : undefined })
      .then((r) => r.data),

  getApproved: (onlyMine = false) =>
    axios
      .get<ExpenseClaim[]>(`${API_URL}/expense-claims/approved`, { params: onlyMine ? { onlyMine: 1 } : undefined })
      .then((r) => r.data),

  getById: (id: number) => axios.get<ExpenseClaim>(`${API_URL}/expense-claims/${id}`).then((r) => r.data),

  create: (data: CreateExpenseClaimDto) =>
    axios.post<ExpenseClaim>(`${API_URL}/expense-claims`, data).then((r) => r.data),

  createAndSubmit: (data: CreateExpenseClaimDto) =>
    axios.post<ExpenseClaim>(`${API_URL}/expense-claims/create-and-submit`, data).then((r) => r.data),

  uploadAttachment: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return axios
      .post<UploadedExpenseAttachment>(`${API_URL}/expense-claims/upload-attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  update: (id: number, data: UpdateExpenseClaimDto) =>
    axios.patch<ExpenseClaim>(`${API_URL}/expense-claims/${id}`, data).then((r) => r.data),

  submit: (id: number) =>
    axios.post<ExpenseClaim>(`${API_URL}/expense-claims/${id}/submit`).then((r) => r.data),

  review: (id: number, status: 'APPROVED' | 'REJECTED', reviewNote?: string) =>
    axios
      .post<ExpenseClaim>(`${API_URL}/expense-claims/${id}/review`, { status, reviewNote })
      .then((r) => r.data),

  getPendingCount: async (): Promise<{ count: number }> => {
    const pending = await expenseClaimsService.getPending();
    return { count: pending.length };
  },

  remove: (id: number) => axios.delete(`${API_URL}/expense-claims/${id}`).then((r) => r.data),
};
